import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import ViewsNav from '../components/ViewsNav';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import BookingModal from '../components/BookingModal';
import ExpenseModal from '../components/ExpenseModal';
import CSVImport from '../components/CSVImport';

const TABS = ['Dashboard', 'Bookings', 'Expenses'];
const ALL_PROPERTIES = 'All Properties';
const EXPENSE_CATEGORIES = ['All', 'Maintenance', 'Cleaning', 'Supplies', 'Insurance', 'HOA', 'Utilities', 'Other'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const PROPERTY_COLORS = {
  "It's Happy Hour": '#00B4C6',
  '4400 Windswept': '#4A7C59',
};

export default function Revenue({ session, currentView, onNavigate }) {
  const [activeTab, setActiveTab] = useState('Dashboard');
  const [bookings, setBookings] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedProperty, setSelectedProperty] = useState(ALL_PROPERTIES);
  const [expenseCategory, setExpenseCategory] = useState('All');
  const [chartView, setChartView] = useState('combined');

  // Modals
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [showCSVImport, setShowCSVImport] = useState(null); // 'bookings' or 'expenses'

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    const [{ data: props }, { data: bk }, { data: exp }] = await Promise.all([
      supabase.from('properties').select('*').order('name'),
      supabase.from('bookings').select('*, properties(name)').order('check_in', { ascending: false }),
      supabase.from('expenses').select('*, properties(name)').order('expense_date', { ascending: false }),
    ]);
    if (props) setProperties(props);
    if (bk) setBookings(bk);
    if (exp) setExpenses(exp);
    setLoading(false);
  };

  // CRUD helpers
  const deleteBooking = async (id) => {
    if (!window.confirm('Delete this booking?')) return;
    const { error } = await supabase.from('bookings').delete().eq('id', id);
    if (!error) setBookings(bookings.filter(b => b.id !== id));
  };

  const deleteExpense = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    const { error } = await supabase.from('expenses').delete().eq('id', id);
    if (!error) setExpenses(expenses.filter(e => e.id !== id));
  };

  const onBookingSaved = (saved, isNew) => {
    if (isNew) setBookings([saved, ...bookings]);
    else setBookings(bookings.map(b => b.id === saved.id ? saved : b));
    setShowBookingModal(false);
    setEditingBooking(null);
  };

  const onExpenseSaved = (saved, isNew) => {
    if (isNew) setExpenses([saved, ...expenses]);
    else setExpenses(expenses.map(e => e.id === saved.id ? saved : e));
    setShowExpenseModal(false);
    setEditingExpense(null);
  };

  // YTD calculations
  const year = new Date().getFullYear();
  const ytdBookings = bookings.filter(b => b.check_in && b.check_in.startsWith(String(year)));
  const ytdExpenses = expenses.filter(e => e.expense_date && e.expense_date.startsWith(String(year)));

  const totalRevenue = ytdBookings.reduce((s, b) => s + (parseFloat(b.net_revenue) || 0), 0);
  const totalExpenses = ytdExpenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const netProfit = totalRevenue - totalExpenses;
  const totalNights = ytdBookings.reduce((s, b) => s + (b.nights || 0), 0);
  const daysInYear = 365;
  const propertiesCount = properties.length || 1;
  const availableNights = daysInYear * propertiesCount;
  const occupancyRate = availableNights > 0 ? ((totalNights / availableNights) * 100).toFixed(1) : 0;

  // Per-property stats
  const propStats = (propName) => {
    const pb = ytdBookings.filter(b => b.properties?.name === propName);
    const pe = ytdExpenses.filter(e => e.properties?.name === propName);
    return {
      gross: pb.reduce((s, b) => s + (parseFloat(b.gross_revenue) || 0), 0),
      expenses: pe.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0),
      net: pb.reduce((s, b) => s + (parseFloat(b.net_revenue) || 0), 0) - pe.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0),
      nights: pb.reduce((s, b) => s + (b.nights || 0), 0),
    };
  };

  // Monthly chart data
  const monthlyData = () => {
    const data = MONTH_NAMES.map((name, i) => {
      const month = String(i + 1).padStart(2, '0');
      const prefix = `${year}-${month}`;

      if (chartView === 'combined') {
        const rev = ytdBookings.filter(b => b.check_in?.startsWith(prefix))
          .reduce((s, b) => s + (parseFloat(b.net_revenue) || 0), 0);
        const exp = ytdExpenses.filter(e => e.expense_date?.startsWith(prefix))
          .reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
        return { name, Revenue: rev, Expenses: exp };
      } else {
        const entry = { name };
        properties.forEach(p => {
          entry[p.name] = ytdBookings
            .filter(b => b.check_in?.startsWith(prefix) && b.properties?.name === p.name)
            .reduce((s, b) => s + (parseFloat(b.net_revenue) || 0), 0);
        });
        return entry;
      }
    });
    return data;
  };

  // Filtered lists
  const filteredBookings = bookings.filter(b =>
    selectedProperty === ALL_PROPERTIES || b.properties?.name === selectedProperty
  );

  const filteredExpenses = expenses.filter(e => {
    const propMatch = selectedProperty === ALL_PROPERTIES || e.properties?.name === selectedProperty;
    const catMatch = expenseCategory === 'All' || e.category === expenseCategory;
    return propMatch && catMatch;
  });

  const fmt = (n) => parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const renderTab = () => {
    switch (activeTab) {
      case 'Dashboard':
        return (
          <div>
            {/* YTD Summary Cards */}
            <div className="revenue-summary-cards">
              <div className="revenue-card">
                <span className="revenue-card-value revenue-green">${fmt(totalRevenue)}</span>
                <span className="revenue-card-label">Net Revenue YTD</span>
              </div>
              <div className="revenue-card">
                <span className="revenue-card-value revenue-red">${fmt(totalExpenses)}</span>
                <span className="revenue-card-label">Expenses YTD</span>
              </div>
              <div className="revenue-card">
                <span className="revenue-card-value revenue-blue">${fmt(netProfit)}</span>
                <span className="revenue-card-label">Net Profit YTD</span>
              </div>
              <div className="revenue-card">
                <span className="revenue-card-value">{occupancyRate}%</span>
                <span className="revenue-card-label">Occupancy Rate</span>
              </div>
            </div>

            {/* Per-property breakdown */}
            <div className="revenue-property-cards">
              {properties.map(p => {
                const stats = propStats(p.name);
                const color = PROPERTY_COLORS[p.name] || '#7B3F6E';
                return (
                  <div key={p.id} className="revenue-property-card" style={{ borderTopColor: color }}>
                    <h4 style={{ color }}>{p.name}</h4>
                    <div className="revenue-property-stats">
                      <div><span className="revenue-green">${fmt(stats.gross)}</span><small>Gross Revenue</small></div>
                      <div><span className="revenue-red">${fmt(stats.expenses)}</span><small>Expenses</small></div>
                      <div><span className="revenue-blue">${fmt(stats.net)}</span><small>Net Profit</small></div>
                      <div><span>{stats.nights}</span><small>Nights Booked</small></div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Monthly Chart */}
            <div className="revenue-chart-section">
              <div className="revenue-chart-header">
                <h3>Monthly Breakdown</h3>
                <div className="revenue-chart-toggle">
                  <button
                    className={`btn btn-sm ${chartView === 'combined' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setChartView('combined')}
                  >Combined</button>
                  <button
                    className={`btn btn-sm ${chartView === 'per-property' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setChartView('per-property')}
                  >Per Property</button>
                </div>
              </div>
              <div className="revenue-chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={monthlyData()} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0d5e8" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `$${v >= 1000 ? (v/1000).toFixed(0) + 'k' : v}`} />
                    <Tooltip formatter={(value) => `$${parseFloat(value).toLocaleString()}`} />
                    <Legend />
                    {chartView === 'combined' ? (
                      <>
                        <Bar dataKey="Revenue" fill="#4A7C59" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Expenses" fill="#d4614a" radius={[4, 4, 0, 0]} />
                      </>
                    ) : (
                      properties.map(p => (
                        <Bar key={p.name} dataKey={p.name} fill={PROPERTY_COLORS[p.name] || '#7B3F6E'} radius={[4, 4, 0, 0]} />
                      ))
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        );

      case 'Bookings':
        return (
          <div>
            <div className="revenue-toolbar">
              <div className="revenue-toolbar-actions">
                <button className="btn btn-secondary btn-sm" onClick={() => setShowCSVImport('bookings')}>
                  Import CSV
                </button>
                <button className="btn btn-primary btn-sm" onClick={() => { setEditingBooking(null); setShowBookingModal(true); }}>
                  + Add Booking
                </button>
              </div>
            </div>

            {filteredBookings.length === 0 ? (
              <div className="empty-state">
                <h3>No bookings</h3>
                <p>Add bookings to start tracking revenue</p>
              </div>
            ) : (
              <div className="revenue-table-wrapper">
                <table className="revenue-table">
                  <thead>
                    <tr>
                      <th>Property</th>
                      <th>Guest</th>
                      <th>Check-in</th>
                      <th>Check-out</th>
                      <th>Nights</th>
                      <th>Gross</th>
                      <th>Fee</th>
                      <th>Net</th>
                      <th>Platform</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBookings.map(b => (
                      <tr key={b.id}>
                        <td className="td-property">{b.properties?.name || '—'}</td>
                        <td>{b.guest_name || '—'}</td>
                        <td>{b.check_in}</td>
                        <td>{b.check_out}</td>
                        <td>{b.nights}</td>
                        <td className="revenue-green">${fmt(b.gross_revenue)}</td>
                        <td className="revenue-red">${fmt(b.platform_fee)}</td>
                        <td className="revenue-blue">${fmt(b.net_revenue)}</td>
                        <td>{b.platform || '—'}</td>
                        <td className="td-actions">
                          <button className="icon-btn" onClick={() => { setEditingBooking(b); setShowBookingModal(true); }}>Edit</button>
                          <button className="icon-btn delete" onClick={() => deleteBooking(b.id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="4"><strong>Total</strong></td>
                      <td><strong>{filteredBookings.reduce((s, b) => s + (b.nights || 0), 0)}</strong></td>
                      <td className="revenue-green"><strong>${fmt(filteredBookings.reduce((s, b) => s + (parseFloat(b.gross_revenue) || 0), 0))}</strong></td>
                      <td className="revenue-red"><strong>${fmt(filteredBookings.reduce((s, b) => s + (parseFloat(b.platform_fee) || 0), 0))}</strong></td>
                      <td className="revenue-blue"><strong>${fmt(filteredBookings.reduce((s, b) => s + (parseFloat(b.net_revenue) || 0), 0))}</strong></td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        );

      case 'Expenses':
        return (
          <div>
            <div className="revenue-toolbar">
              <div className="revenue-toolbar-filters">
                {EXPENSE_CATEGORIES.map(c => (
                  <button
                    key={c}
                    className={`btn btn-sm ${expenseCategory === c ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setExpenseCategory(c)}
                  >{c}</button>
                ))}
              </div>
              <div className="revenue-toolbar-actions">
                <button className="btn btn-secondary btn-sm" onClick={() => setShowCSVImport('expenses')}>
                  Import CSV
                </button>
                <button className="btn btn-primary btn-sm" onClick={() => { setEditingExpense(null); setShowExpenseModal(true); }}>
                  + Add Expense
                </button>
              </div>
            </div>

            {filteredExpenses.length === 0 ? (
              <div className="empty-state">
                <h3>No expenses</h3>
                <p>Add expenses to track property costs</p>
              </div>
            ) : (
              <div className="revenue-table-wrapper">
                <table className="revenue-table">
                  <thead>
                    <tr>
                      <th>Property</th>
                      <th>Title</th>
                      <th>Category</th>
                      <th>Date</th>
                      <th>Vendor</th>
                      <th>Amount</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredExpenses.map(e => (
                      <tr key={e.id}>
                        <td className="td-property">{e.properties?.name || '—'}</td>
                        <td>{e.title}</td>
                        <td><span className="tag">{e.category}</span></td>
                        <td>{e.expense_date}</td>
                        <td>{e.vendor || '—'}</td>
                        <td className="revenue-red">${fmt(e.amount)}</td>
                        <td className="td-actions">
                          <button className="icon-btn" onClick={() => { setEditingExpense(e); setShowExpenseModal(true); }}>Edit</button>
                          <button className="icon-btn delete" onClick={() => deleteExpense(e.id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="5"><strong>Total</strong></td>
                      <td className="revenue-red"><strong>${fmt(filteredExpenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0))}</strong></td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="app-layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1>Property Manager</h1>
          <p>{session.user.email}</p>
        </div>

        <ViewsNav currentView={currentView} onNavigate={onNavigate} />

        <div className="sidebar-section">
          <div className="sidebar-section-label">Properties</div>
          <div
            className={`sidebar-item ${selectedProperty === ALL_PROPERTIES ? 'active' : ''}`}
            onClick={() => setSelectedProperty(ALL_PROPERTIES)}
          >
            All Properties
          </div>
          {properties.map(p => (
            <div
              key={p.id}
              className={`sidebar-item ${selectedProperty === p.name ? 'active' : ''}`}
              onClick={() => setSelectedProperty(p.name)}
            >
              {p.name}
            </div>
          ))}
        </div>

        <div className="sidebar-footer">
          <button className="logout-btn" onClick={() => supabase.auth.signOut()}>Sign Out</button>
        </div>
      </aside>

      <main className="main-content">
        <div className="hub-tabs">
          {TABS.map(tab => (
            <button
              key={tab}
              className={`hub-tab ${activeTab === tab ? 'active' : ''}`}
              style={activeTab === tab ? { borderBottomColor: '#7B3F6E', color: '#7B3F6E' } : {}}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="content-area">
          {loading ? (
            <div className="empty-state"><p>Loading revenue data...</p></div>
          ) : (
            renderTab()
          )}
        </div>
      </main>

      {showBookingModal && (
        <BookingModal
          booking={editingBooking}
          properties={properties}
          onSaved={onBookingSaved}
          onClose={() => { setShowBookingModal(false); setEditingBooking(null); }}
        />
      )}

      {showExpenseModal && (
        <ExpenseModal
          expense={editingExpense}
          properties={properties}
          onSaved={onExpenseSaved}
          onClose={() => { setShowExpenseModal(false); setEditingExpense(null); }}
        />
      )}

      {showCSVImport && (
        <CSVImport
          type={showCSVImport}
          properties={properties}
          onImported={fetchAll}
          onClose={() => setShowCSVImport(null)}
        />
      )}
    </div>
  );
}
